/**
 * "Where the money goes" strip, shown at the top of the Tax Reserves overview.
 *
 * WHY (2026-08-30, Fan): "I don't see the split for Profit take home and
 * working capital". The split existed only inside the Cash waterfall tab, so
 * the first screen of the page still answered a different question. This puts
 * the six numbers he asked for on the page he actually lands on, and leaves the
 * per-week / per-invoice detail behind the waterfall tab.
 *
 * Cash received to date only: a payment that has not arrived cannot fund a
 * reserve, so nothing future is counted here by design.
 */
import { useState, useEffect } from 'react';
import { Loader2, Info } from 'lucide-react';
import { brosTaxReservesAPI } from '../lib/api';
import { formatCurrency } from '../lib/chartFormatters';
import { getErrorMessage } from '../lib/errors';

function num(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

const COLUMNS = [
  {
    key: 'received',
    label: 'Received',
    hint: 'Client cash actually in the bank, to date.',
    strong: true,
  },
  {
    key: 'supplier_cost',
    label: 'Working capital',
    hint: 'The supplier cost behind that cash — yours to pass on, not to keep.',
  },
  {
    key: 'net_vat_due',
    label: 'Net VAT due',
    hint: 'Output VAT on the invoices less input VAT the suppliers charged you.',
  },
  {
    key: 'corp_tax',
    label: 'Corp tax (15%)',
    hint: 'Estimated at 15% of the profit the received cash represents.',
  },
  {
    key: 'draw_available',
    label: 'Take home (50%)',
    hint: 'Half of the net profit after VAT and corp tax.',
    strong: true,
  },
  {
    key: 'overhead_retained',
    label: 'Overhead (50%)',
    hint: 'The other half, held back for overhead.',
    muted: true,
  },
];

export function BROSMoneySplitSummary({ onSeeDetail }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const response = await brosTaxReservesAPI.getCashWaterfall({
          group: 'month',
          corp_tax_pct: '15',
          draw_pct: '50',
        });
        if (live) {
          setData(response.data);
          setError(null);
        }
      } catch (err) {
        if (live) setError(getErrorMessage(err));
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, []);

  const totals = Object.entries(data?.totals_by_currency ?? {});
  const unresolvedCount = totals.reduce(
    (sum, [, row]) => sum + num(row.unresolved_supplier_count), 0);

  return (
    <div data-testid="bros-money-split-summary">
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
          Where the money goes &middot; received to date
        </h2>
        {onSeeDetail && (
          <button
            type="button"
            className="text-xs text-primary hover:underline"
            onClick={onSeeDetail}
            data-testid="money-split-see-detail"
          >
            See it per week and per invoice
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : error ? (
        <p className="py-6 text-center text-sm text-destructive" data-testid="money-split-error">{error}</p>
      ) : totals.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          No client payments received yet, so there is nothing to split.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground border-b bg-muted/40">
              <tr>
                <th className="text-left font-medium py-2 pl-3">Currency</th>
                {COLUMNS.map((column) => (
                  <th key={column.key} className="text-right font-medium py-2 pr-3" title={column.hint}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {totals.map(([currency, row]) => (
                <tr key={currency} className="border-b last:border-0" data-testid={`money-split-${currency}`}>
                  <td className="py-2 pl-3 font-mono text-xs">{currency}</td>
                  {COLUMNS.map((column) => (
                    <td
                      key={column.key}
                      className={`py-2 pr-3 text-right tabular-nums ${column.strong ? 'font-semibold' : ''} ${column.muted ? 'text-muted-foreground' : ''}`}
                    >
                      {formatCurrency(num(row[column.key]), currency)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {unresolvedCount > 0 && (
        <p className="text-xs text-muted-foreground flex items-start gap-1 mt-2">
          <Info className="h-3 w-3 mt-0.5 shrink-0" />
          {unresolvedCount} payments have no known supplier cost yet, so they fund no take-home until that cost
          is in.
        </p>
      )}
    </div>
  );
}

export default BROSMoneySplitSummary;
